import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { META_DERNIER_EXPORT, db, lireMeta } from '../../db/db'
import { effacerTout, exporterSauvegarde, importerSauvegarde } from '../../db/sauvegarde'
import { installerReferentiel } from '../../db/seed'
import { estInstallee, lireQuota, type QuotaStockage } from '../../pwa/register'
import { JOUR_MS, relatif } from '../../domain/time'
import { Carte, Confirmation, Entete, Puce } from '../composants'

function formaterOctets(n: number): string {
  if (n < 1024) return `${n} o`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`
  return `${(n / 1024 / 1024).toFixed(1)} Mo`
}

export function PageReglages() {
  const [quota, setQuota] = useState<QuotaStockage | null>(null)
  const [dernierExport, setDernierExport] = useState<string | null>(null)
  const [message, setMessage] = useState<{ ton: 'ok' | 'danger'; texte: string } | null>(null)
  const [confirmerEffacement, setConfirmerEffacement] = useState(false)
  const [compteurs, setCompteurs] = useState({ repas: 0, sensations: 0, selles: 0, recettes: 0, jours: 0 })
  const fichierRef = useRef<HTMLInputElement>(null)

  const rafraichir = async () => {
    setQuota(await lireQuota())
    setDernierExport(await lireMeta<string | null>(META_DERNIER_EXPORT, null))
    setCompteurs({
      repas: await db.meals.count(),
      sensations: await db.sensations.count(),
      selles: await db.stools.count(),
      recettes: await db.recipes.count(),
      jours: await db.dailyLogs.count(),
    })
  }

  useEffect(() => {
    void rafraichir()
  }, [])

  const exporter = async () => {
    const r = await exporterSauvegarde()
    const textes: Record<typeof r, string> = {
      partage: 'Sauvegarde partagée.',
      telecharge: 'Sauvegarde téléchargée.',
      copie: 'Sauvegarde copiée dans le presse-papier. Collez-la dans une note ou un mail.',
      echec: "L'export n'a pas abouti (annulé ou refusé par le navigateur).",
    }
    setMessage({ ton: r === 'echec' ? 'danger' : 'ok', texte: textes[r] })
    await rafraichir()
  }

  const importer = async (fichier: File, mode: 'fusion' | 'remplacement') => {
    try {
      const texte = await fichier.text()
      const r = await importerSauvegarde(texte, mode)
      setMessage({
        ton: 'ok',
        texte: `Import réussi : ${r.meals} repas, ${r.sensations} sensations, ${r.stools} passages, ${r.recipes} recettes, ${r.dailyLogs} jours.`,
      })
      await rafraichir()
    } catch (e) {
      setMessage({ ton: 'danger', texte: (e as Error).message })
    }
  }

  const exportVieux =
    dernierExport === null || Date.now() - Date.parse(dernierExport) > 14 * JOUR_MS

  return (
    <>
      <Entete titre="Réglages" />

      <main className="contenu">
        {message && (
          <div className={message.ton === 'ok' ? 'encart encart--info' : 'encart encart--danger'}>
            {message.texte}
          </div>
        )}

        {/* --- Sauvegarde --- */}
        <Carte
          titre="Sauvegarde"
          action={exportVieux ? <Puce ton="alerte">À faire</Puce> : <Puce ton="ok">À jour</Puce>}
        >
          <p className="doux" style={{ marginBottom: 12 }}>
            Toutes vos données vivent uniquement sur cet appareil. Il n'existe aucune copie
            ailleurs : si le téléphone est perdu, réinitialisé, ou l'application désinstallée,
            tout disparaît. L'export est le seul filet de sécurité.
          </p>
          <p className="tres-doux" style={{ marginBottom: 12 }}>
            {dernierExport
              ? `Dernière sauvegarde ${relatif(dernierExport)}.`
              : 'Aucune sauvegarde effectuée.'}
          </p>

          <div className="boutons">
            <button type="button" className="btn btn--principal grandir" onClick={() => void exporter()}>
              Exporter mes données
            </button>
            <button
              type="button"
              className="btn grandir"
              onClick={() => fichierRef.current?.click()}
            >
              Importer
            </button>
          </div>

          <input
            ref={fichierRef}
            type="file"
            accept="application/json,.json"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) void importer(f, 'fusion')
              e.target.value = ''
            }}
          />

          <p className="tres-doux" style={{ marginTop: 10 }}>
            L'import fusionne : les enregistrements de même identifiant sont mis à jour, les autres
            conservés. Rien n'est supprimé.
          </p>
        </Carte>

        {/* --- Contenu --- */}
        <Carte titre="Contenu de la base">
          <div className="puces">
            <Puce>{compteurs.repas} repas</Puce>
            <Puce>{compteurs.sensations} sensations</Puce>
            <Puce>{compteurs.selles} passages aux selles</Puce>
            <Puce>{compteurs.recettes} recettes</Puce>
            <Puce>{compteurs.jours} jours de journal</Puce>
          </div>
          {quota && (
            <p className="tres-doux" style={{ marginTop: 12 }}>
              Espace utilisé : {formaterOctets(quota.utiliseOctets)}
              {quota.quotaOctets > 0 && ` sur ${formaterOctets(quota.quotaOctets)}`}.{' '}
              {quota.persistant
                ? 'Le stockage est marqué persistant : le navigateur ne le purgera pas automatiquement.'
                : "Le stockage n'est pas marqué persistant. Installez l'application sur l'écran d'accueil pour réduire le risque de purge."}
            </p>
          )}
        </Carte>

        {/* --- Installation --- */}
        <Carte titre="Application">
          <div className="ligne ligne--espacee" style={{ marginBottom: 12 }}>
            <span className="doux">Mode installé</span>
            {estInstallee() ? <Puce ton="ok">Oui</Puce> : <Puce ton="alerte">Non</Puce>}
          </div>
          <Link to="/installation" className="btn btn--large">
            Installer sur l'écran d'accueil
          </Link>
        </Carte>

        {/* --- Referentiel --- */}
        <Carte titre="Référentiel d'ingrédients">
          <p className="doux" style={{ marginBottom: 12 }}>
            Réinstalle les ingrédients livrés qui auraient été supprimés. Vos ingrédients
            personnels et vos modifications ne sont jamais écrasés.
          </p>
          <button
            type="button"
            className="btn btn--large"
            onClick={() => {
              void db.meta
                .delete('seedVersion')
                .then(() => installerReferentiel())
                .then((r) =>
                  setMessage({ ton: 'ok', texte: `${r.ajoutes} ingrédient(s) réinstallé(s).` }),
                )
            }}
          >
            Réinstaller les ingrédients manquants
          </button>
        </Carte>

        {/* --- Zone dangereuse --- */}
        <Carte titre="Effacer les données">
          <p className="doux" style={{ marginBottom: 12 }}>
            Supprime définitivement repas, symptômes, recettes et journal. Exportez d'abord.
          </p>
          <button
            type="button"
            className="btn btn--danger btn--large"
            onClick={() => setConfirmerEffacement(true)}
          >
            Tout effacer
          </button>
        </Carte>

        {confirmerEffacement && (
          <Confirmation
            titre="Tout effacer ?"
            message="Toutes vos données seront définitivement supprimées de cet appareil. Cette action est irréversible et il n'existe aucune copie sur un serveur."
            libelleConfirmer="Tout effacer"
            danger
            onConfirmer={() => {
              setConfirmerEffacement(false)
              void effacerTout()
                .then(() => installerReferentiel())
                .then(() => rafraichir())
                .then(() => setMessage({ ton: 'ok', texte: 'Données effacées.' }))
            }}
            onAnnuler={() => setConfirmerEffacement(false)}
          />
        )}

        <p className="tres-doux centre" style={{ marginTop: 8 }}>
          Suivi SCI — application locale, sans compte et sans serveur.
          <br />
          Aucune donnée ne quitte cet appareil.
        </p>
      </main>
    </>
  )
}
